import React, { useState, useEffect } from "react";
import { useCompany } from "../../hooks/useCompany";
import { IoMdClose } from "react-icons/io";
import { InsuranceProvidersType } from "../../types/loadedData";

interface ProviderDetailsProps {
  title: string;
  description: string;
  logo: null | File;
}

interface InsuranceProviderModalProps {
  isOpen: boolean;
  onClose: () => void;
  fetch: () => void;
  provider: InsuranceProvidersType | null;
  setProvider: React.Dispatch<
    React.SetStateAction<InsuranceProvidersType | null>
  >;
}

const InsuranceProviderModal: React.FC<InsuranceProviderModalProps> = ({
  isOpen,
  onClose,
  fetch,
  provider,
  setProvider,
}) => {
  const { insuranceProvider, updateInsuranceProvider } = useCompany();
  const [loading, setLoading] = useState(false);
  const [providerDetails, setProviderDetails] = useState<ProviderDetailsProps>({
    title: "",
    description: "",
    logo: null,
  });
  const [errors, setErrors] = useState({
    title: "",
    description: "",
    logo: "",
  });

  useEffect(() => {
    if (provider) {
      setProviderDetails({
        title: provider.title,
        description: provider.description,
        logo: null,
      });
    } else {
      setProviderDetails({
        title: "",
        description: "",
        logo: null,
      });
    }
  }, [provider]);

  if (!isOpen) return null;

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setProviderDetails((prev) => ({ ...prev, [name]: value }));
    if (name === "title" && value) {
      setErrors((prev) => ({ ...prev, title: "" }));
    }
    if (name === "description" && value) {
      setErrors((prev) => ({ ...prev, description: "" }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files ? e.target.files[0] : null;
    if (file && file.type.startsWith("image/")) {
      setProviderDetails((prev) => ({ ...prev, logo: file }));
      setErrors((prev) => ({ ...prev, logo: "" }));
    } else {
      setErrors((prev) => ({
        ...prev,
        logo: "Please upload a valid image file.",
      }));
    }
  };

  const handleSubmit = async () => {
    let valid = true;
    let newErrors = { title: "", description: "", logo: "" };

    if (!providerDetails.title.trim()) {
      newErrors.title = "Title is required.";
      valid = false;
    }

    if (!providerDetails.description) {
      newErrors.description = "Description is required";
      valid = false;
    }

    if (!providerDetails.logo) {
      newErrors.logo = "Logo is required.";
      valid = false;
    }

    setErrors(newErrors);

    if (!valid) return;

    const formData = new FormData();
    formData.append("title", providerDetails.title);
    formData.append("description", providerDetails.description);

    if (providerDetails.logo) {
      formData.append("logo", providerDetails.logo);
    }

    setLoading(true);

    try {
      if (provider) {
        // Update existing provider
        await updateInsuranceProvider({ id: provider._id, payload: formData });
      } else {
        // Add new provider
        await insuranceProvider({ payload: formData });
      }
      fetch();
      onClose();
      setProvider(null);
    } catch (error) {
      console.error("Error submitting provider:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between">
          <h2 className="text-lg font-semibold mb-4">Add Insurance Provider</h2>
          <p onClick={onClose} className="text-lg cursor-pointer">
            <IoMdClose />
          </p>
        </div>
        <form>
          <div className="mb-4">
            <label className="block text-gray-900 mb-1 font-bold">Title</label>
            <input
              type="text"
              name="title"
              value={providerDetails.title}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border rounded"
              placeholder="Provider Title"
            />
            {errors.title && (
              <p className="text-red-500 text-sm">{errors.title}</p>
            )}
          </div>
          <div className="mb-4">
            <label className="block text-gray-900 mb-1 font-bold">
              Description
            </label>
            <textarea
              name="description"
              value={providerDetails.description}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border rounded"
              placeholder="Provider Description"
            />
            {errors.description && (
              <p className="text-red-500 text-sm">{errors.description}</p>
            )}
          </div>
          <div className="mb-4">
            <label className="block text-gray-900 mb-1 font-bold">
              Insurance-provider logo
            </label>

            <div className={Classname.logoContainer}>
              <div className={Classname.logoBox}>
                {providerDetails.logo ? (
                  <img
                    src={URL.createObjectURL(providerDetails.logo)}
                    alt="Selected Logo"
                    className="object-contain w-full h-full"
                  />
                ) : (
                  <img src="/imageplaceholder.png" alt="Placeholder" />
                )}
              </div>
              <label className={Classname.uploadLabel} htmlFor="logoUpload">
                Upload Logo
              </label>
              <input
                type="file"
                id="logoUpload"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
              <img src="/iconUpload.svg" alt="" className="h-5" />
            </div>
            {errors.logo && (
              <p className="text-red-500 text-sm">{errors.logo}</p>
            )}
          </div>
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={onClose}
              className={Classname.cancelButton}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className={Classname.saveButton}
            >
              {loading ? "Please wait..." : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Classname = {
  logoContainer:
    "flex items-center mt-2 border rounded-lg border-gray-200 p-3 w-fit gap-2",
  logoBox: "w-14 h-10 bg-gray-200 flex items-center justify-center rounded-md",
  uploadLabel: "text-blue-500 ml-2 cursor-pointer",
  select:
    "w-full border border-gray-300 p-2 mt-2 rounded-md h-10 cursor-pointer",
  addButton: "bg-white text-blue-500 px-4 py-2 rounded mt-4",
  buttonContainer: "mt-4 flex justify-end",
  cancelButton: "px-4 py-2 rounded-lg border-blue-500 border",
  saveButton: "ml-2 bg-blue-500 text-white px-4 py-2 rounded-lg",
};

export default InsuranceProviderModal;
